/* global process */
/**
 * @module lib/XmlUtils
 */
import axios, { type AxiosResponse } from 'axios';
import map from 'lodash/map';

import { API_PATH } from '../../constants/ApiActions';
import ParseError from '../../lib/error/ParseError';
import XhrError from '../../lib/error/XhrError';
import Logger from '../../tools/Logger';
import { type ValueOrArray } from '../../types';

const log = Logger.child({ component: 'lib/XmlUtils' }, true);

/**
 * This is a helper method used to (barely) simplify the process of using
 * the efficient XPATH xml parser routines. These routines are supposedly
 * available on all of our supported platforms.
 *
 * @param {object} xmlDoc The parsed XML Document object
 * @param {string} xpath The xpath query string
 * @param {number} xtype The enumerated XPathResult type
 */
export const evaluate = (
  xmlDoc: XMLDocument,
  xpath: string,
  xtype = XPathResult.ANY_TYPE,
): XPathResult => {
  try {
    return xmlDoc.evaluate(xpath, xmlDoc, null, xtype, null);
  } catch (e) {
    // For IE. Using wgxpath to extend document with implementation of DOM Level 3 XPath specification.
    // Can't use this method on most modern browsers since you can't use the window document to evaluate
    // another node.
    return document.evaluate(xpath, xmlDoc, null, xtype, null);
  }
};

/**
 * Used internally to extract the correct value and value type from the xml.
 * It should be noted that if alternative types are encountered later this will
 * need to be modified to support them - including any _lists_ such that this
 * could recurse.
 *
 * @param {object} node The actual DOM object/element
 */
const extractValue = (node: Element | null): ValueOrArray<string | number | null> => {
  if (!node || (!node.nodeName && !node.textContent)) {
    return null;
  }
  const type = node.nodeName;
  const value = node.textContent ?? '';
  // @todo currently only seen types are "string", "int" & "list", if others are
  // possible then this will need to be extended
  // @note CDATA wrapped strings are automatically normalized by the xml
  // parser and we don't need to do that again
  if (type === 'int') {
    return parseInt(value, 10);
  } else if (type === 'list') {
    return map(node.children, childNode => {
      return extractValue(childNode);
    });
  }
  return value;
};

// @todo this SHOULD be available on ALL platforms we support but if for any reason
// the MS platforms are failing we just need a polyfill. Should test before
// requiring the polyfill and would be best to only include it when necessary.
const parser: DOMParser | null = process.env.NODE_ENV === 'test' ? null : new DOMParser();

/*
 *  DFP XML sometimes begins with a comment making it invalid XML.
 *  Strip out anything leading upto the XML declaration
 */
const stripLeadingComment = (xml: string): string => {
  /* xml declaration must be at the 1st pos of 1st line */
  const index = xml.indexOf('<?xml ');
  if (index === 0 || index === -1) {
    return xml;
  }
  return xml.substr(index);
};

/**
 * Common utility function for xml payloads that contain members that can be
 * parsed into a native JavaScript object of key-value pairs based on the
 * response value type. Does not include empty members (they must contain
 * a _name_ and a _value_ even if the value is an empty string).
 *
 * To understand the structure of these payloads see:
 * https://wiki.savagebeast.com/display/eng/Web+Video+XML
 * https://wiki.savagebeast.com/display/eng/Web+Audio+XML
 *
 * @param {object} xmlDoc The parsed XML Document object
 */
export const parseXmlMembers = (xmlDoc: XMLDocument): typeof result => {
  const members = evaluate(xmlDoc, '//member[name and value]');
  const result: Record<string, ReturnType<typeof extractValue>> = {};
  let node = members.iterateNext() as Element;
  while (node) {
    // we know we have a member node that has a name and value entry
    // the name _value_ is pretty straight-forward but the value _value_ may
    // be more trying to get into a useable state
    const name = node.querySelector('name')?.textContent ?? '';
    const valueElement = node.querySelector('value');

    if (valueElement) {
      // and now we can safely assign the value to the property name without
      // knowing the schema
      result[name] = extractValue(valueElement.firstElementChild);
    }
    // check to see if there are any more entries
    node = members.iterateNext() as Element;
  }
  // We did not successfully extract any nodes from XML document
  if (Object.keys(result).length === 0) {
    throw new ParseError('Could not extract any values');
  }
  return result;
};

export const parseXml = (content: string, returnSourceXml = false): typeof parsedXml => {
  const cleanedContent = stripLeadingComment(content);
  const xmlDoc = parser?.parseFromString(cleanedContent, 'text/xml');
  const parsedXml = returnSourceXml ? { xmlDoc, source: content } : xmlDoc;
  return parsedXml;
};

// default config, absolute url's will override the default but allow for
// relative url's when possible
const xhr = axios.create({
  baseURL: API_PATH,
  responseType: 'text',
});

// we will automatically parse the responses into xml documents
xhr.interceptors.response.use(
  result => result.data,
  e => Promise.reject(new XhrError(e.message)),
);

/**
 * Method used to fetch remote XML resources and automatically parses the
 * response. This is not intended to be used with services and does not
 * pass XSRF tokens or X-AuthToken headers.
 *
 * @param {string} url The url to fetch
 */
export const fetchXml = ({
  url,
}: {
  url: string;
}): Promise<string | AxiosResponse<unknown, unknown>> => {
  log.trace(`Attempting to fetch xml from "${url}"`);
  return xhr
    .get(url)
    .then(response => {
      return response;
    })
    .catch(error => {
      if (error instanceof XhrError) {
        // Intermediate catch only to inject url
        error.url = url;
      }
      return Promise.reject(error);
    });
};

/**
 * Used simply to ensure the id returned from the server is in the expected
 * format of all digits.
 *
 * @param {string} value The id string to validate
 */
const validateId = (value: string): string => (/[0-9]*/.test(value) ? value : '');

/**
 * Takes the parsed xml payload (as an xml document object) and parses it further
 * for the correct creative/line id's (from a comment...in xml) then for the
 * member data. This method is agnostic as to whether or not it applies to
 * the audio or video ad workflow.
 *
 * @param {object} xmlDoc The parsed XML Document object
 */
export const convertXmlToJs = (xmlDoc: XMLDocument): typeof result => {
  const result = parseXmlMembers(xmlDoc);
  // we still need to fetch the comment with the creative and line id's as they
  // aren't members of the xml payload
  const node = evaluate(
    xmlDoc,
    '//comment()[1]',
    XPathResult.FIRST_ORDERED_NODE_TYPE,
  ).singleNodeValue;
  if (!node || !node.textContent) {
    result.parseError = 'Missing node or textContent field of node';
    return result;
  }
  const [lineId, creativeId] = node.textContent
    .trim()
    .split(':')
    .map(id => (id ? id.trim() : ''));
  result.lineId = validateId(lineId);
  result.creativeId = validateId(creativeId);
  if (!result.lineId || !result.creativeId) {
    result.parseError = `Invalid creativeId or lineId parsed. Parsed from: ${node.textContent}`;
  }
  return result;
};
